import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { useRadarChart, RADAR_AXES, AXIS_LABELS } from '../../context/shared/RadarChartContext'
import { useRadarTrends } from '../../hooks/useRadarTrends';
import { useTheme } from '../../context/shared/ThemeContext'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const MentalHealthRadarChart: React.FC = () => {
  // Using light theme only - no dark mode
  const { radarData, loading, error } = useRadarChart();
  const { trends, hasHistoricalData, overallTrend } = useRadarTrends();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <p className="animate-pulse text-gray-600">Loading insights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-[400px]">
        <p className="text-red-600 p-4 text-center">{error}</p>
      </div>
    );
  }

  if (!radarData) {
     return (
       <div className="flex justify-center items-center h-[400px] text-center">
         <div>
           <p className="text-lg text-gray-700">No symbolic data available.</p>
           <p className="text-sm text-gray-500">Chat more to generate your first report.</p>
         </div>
       </div>
     );
  }

  // Create enhanced labels with trend indicators
  const enhancedLabels = RADAR_AXES.map(axis => {
    const trend = trends.find(t => t.axis === axis);
    const baseLabel = AXIS_LABELS[axis] || axis;
    
    if (!trend || !hasHistoricalData) {
      return baseLabel;
    }

    let trendIndicator = '';
    let trendColor = '';
    
    switch (trend.trend) {
      case 'up':
        trendIndicator = ' ↑';
        trendColor = 'text-green-600';
        break;
      case 'down':
        trendIndicator = ' ↓';
        trendColor = 'text-red-600';
        break;
      case 'stable':
        trendIndicator = ' →';
        trendColor = 'text-gray-500';
        break;
      default:
        trendIndicator = ' —';
        trendColor = 'text-gray-400';
    }

    return `${baseLabel}${trendIndicator}`;
  });
  
  const chartData: ChartData<"radar", number[], string> = {
    labels: enhancedLabels,
    datasets: [
      {
        label: 'Your Symbolic Profile',
        data: RADAR_AXES.map(axis => radarData.axes[axis] || 0),
        backgroundColor: 'rgba(79, 70, 229, 0.2)',
        borderColor: 'rgb(79, 70, 229)',
        borderWidth: 2,
      }
    ]
  };

  const options: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max: 100,
        beginAtZero: true,
        ticks: { stepSize: 20, showLabelBackdrop: false, color: '#4B5563' },
        grid: { color: 'rgba(209, 213, 219, 0.5)' },
        angleLines: { color: 'rgba(209, 213, 219, 0.5)' },
        pointLabels: {
          color: '#1F2937',
          font: { size: windowWidth < 640 ? 10 : 12 },
          callback: function(value, index) {
            const axis = RADAR_AXES[index];
            const trend = trends.find(t => t.axis === axis);
            
            if (!trend || !hasHistoricalData) {
              return value;
            }

            // Add percentage change to tooltip
            if (trend.percentageChange !== null) {
              const sign = trend.percentageChange > 0 ? '+' : '';
              return `${value} (${sign}${trend.percentageChange.toFixed(1)}%)`;
            }
            
            return value;
          }
        }
      }
    },
    plugins: {
      legend: { display: true, position: 'top' as const, labels: { color: '#1F2937' } },
      tooltip: {
        callbacks: {
          label: function(context) {
            const axis = RADAR_AXES[context.dataIndex];
            const trend = trends.find(t => t.axis === axis);
            const baseLabel = context.dataset.label || 'Value';
            
            if (!trend || !hasHistoricalData) {
              return `${baseLabel}: ${context.parsed.r}`;
            }

            let trendText = '';
            if (trend.delta !== null) {
              const sign = trend.delta > 0 ? '+' : '';
              trendText = ` (${sign}${trend.delta.toFixed(1)} from last week)`;
            }

            return `${baseLabel}: ${context.parsed.r}${trendText}`;
          }
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Trend Summary */}
      {hasHistoricalData && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">
              Weekly Trend:
            </span>
            <span className={`text-sm font-semibold ${
              overallTrend === 'improving' ? 'text-green-600' :
              overallTrend === 'declining' ? 'text-red-600' :
              'text-gray-600'
            }`}>
              {overallTrend === 'improving' && '↗️ Improving'}
              {overallTrend === 'declining' && '↘️ Declining'}
              {overallTrend === 'stable' && '→ Stable'}
              {overallTrend === 'no-data' && '— No Data'}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Compared to last week
          </div>
        </div>
      )}

      {/* Radar Chart */}
      <div className="relative h-[400px] w-full">
        <Radar data={chartData} options={options} />
      </div>

      {/* Trend Legend */}
      {hasHistoricalData && (
        <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <span className="text-green-600">↑</span>
            <span>Improving</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-red-600">↓</span>
            <span>Declining</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-gray-500">→</span>
            <span>Stable</span>
          </div>
          <div className="flex items-center space-x-1">
            <span className="text-gray-400">—</span>
            <span>No Data</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentalHealthRadarChart; 